import { PaymentCustomField, RestfulPayPaymentSystem } from '@nano/payment';

export interface PaymentAdditionalInfo extends IsPaymentHasAdditionalInfo {
  payerAddressMandatory?: boolean;
  payerAddress?: string;
  payerCountry?: string;
  payerProvince?: string;
  payerCity?: string;
  payerStreetName?: string;
  payerBuildingNumber?: string;
  payerBuildingName?: string;
  payerPostalCode?: string;
  // system inconsistency: payment
  correspondentBankIdentifier?: string;
  correspondentBankName?: string;
  correspondentBankAddress?: string;
  correspondentBankCode?: string;
  correspondentBankCountry?: string;
  // system inconsistency: payment entry
  intermediaryInstitutionIdentifier?: string;
  intermediaryInstitutionName?: string;
  intermediaryInstitutionAddress?: string;
  intermediaryInstitutionCode?: string;
  intermediaryInstitutionCountry?: string;

  paymentIdentification?: string;
  sendPayerAddress?: boolean;
  structuredAddressEnabled?: boolean;

  payerIdentification?: string;
  payerIdentificationType?: string;
  payerIdentificationCodeType?: string;
  payerId?: string;
  payerBirthDate?: string;
  payerCountryOfBirth?: string;
  payerProvinceOfBirth?: string;
  payerCityOfBirth?: string;
  payerIdentificationIssueCountry?: string;
  payerIdentificationIssueDate?: string;
  payerIdentificationExpiryDate?: string;
  payerRegistrationDate?: string;
  payerNationality?: string;

  initialPayerName?: string;
  initialPayerIdentification?: string;
  initialPayerIdentificationType?: string;
  initialPayerIdentificationCodeType?: string;
  initialPayerLegalEntityIdentifier?: string;
  initialPayerBic?: string;
  initialPayerId?: string;
  initialPayerBirthDate?: string;
  initialPayerProvinceOfBirth?: string;
  initialPayerCityOfBirth?: string;
  initialPayerCountryOfBirth?: string;

  initialPayerPhoneNumber?: string;
  initialPayerEmail?: string;

  initialPayerAddressType?: string;
  initialPayerAddress?: string;
  initialPayerCountry?: string;
  initialPayerCity?: string;
  initialPayerProvince?: string;
  initialPayerStreetName?: string;
  initialPayerBuildingName?: string;
  initialPayerBuildingNumber?: string;
  initialPayerPostalCode?: string;

  beneficiaryIdentification?: string;
  beneficiaryIdentificationName?: string;
  beneficiaryIdentificationSurname?: string;
  beneficiaryIdentificationType?: string;
  beneficiaryIdentificationCodeType?: string;
  beneficiaryIdentificationCode?: string;
  beneficiaryIdentificationDocument?: string;
  beneficiaryAccountType?: string;
  beneficiaryId?: string;
  beneficiaryBirthDate?: string;
  beneficiaryCountryOfBirth?: string;
  beneficiaryProvinceOfBirth?: string;
  beneficiaryCityOfBirth?: string;

  beneficiaryPhoneNumber?: string;
  beneficiaryEmail?: string;
  accountInBeneficiaryBank?: string;
  sourceOfIncome?: string;
  beneficiaryNationality?: string;
  beneficiaryRegistrationCountry?: string;
  purposeCode?: string;
  routingCode1?: string;
  routingCode2?: string;

  beneficiaryAddressType?: string;
  beneficiaryCountry?: string;
  beneficiaryProvince?: string;
  beneficiaryCity?: string;
  beneficiaryAddress?: string;
  beneficiaryStreetName?: string;
  beneficiaryBuildingNumber?: string;
  beneficiaryBuildingName?: string;
  beneficiaryPostalCode?: string;

  ultimateBeneficiaryName?: string;
  ultimateBeneficiaryIdentification?: string;
  ultimateBeneficiaryIdentificationType?: string;
  ultimateBeneficiaryIdentificationCodeType?: string;
  ultimateBeneficiaryLegalEntityIdentifier?: string;
  ultimateBeneficiaryBic?: string;
  ultimateBeneficiaryId?: string;
  ultimateBeneficiaryCountryOfBirth?: string;
  ultimateBeneficiaryBirthDate?: string;
  ultimateBeneficiaryProvinceOfBirth?: string;
  ultimateBeneficiaryCityOfBirth?: string;

  ultimateBeneficiaryPhoneNumber?: string;
  ultimateBeneficiaryEmail?: string;

  ultimateBeneficiaryAddress?: string;
  ultimateBeneficiaryAddressType?: string;
  ultimateBeneficiaryCountry?: string;
  ultimateBeneficiaryCity?: string;
  ultimateBeneficiaryProvince?: string;
  ultimateBeneficiaryStreetName?: string;
  ultimateBeneficiaryBuildingName?: string;
  ultimateBeneficiaryBuildingNumber?: string;
  ultimateBeneficiaryPostalCode?: string;

  additionalBeneficiaryBankCodeType?: string;
  additionalBeneficiaryBankCode?: string;

  accountWithBankCorrespondent?: string;
  additionalBeneficiaryCorrespondentBankCodeType?: string;
  additionalBeneficiaryCorrespondentBankCode?: string;

  beneficiaryBankFromClassifiers?: boolean;
  intermediaryBankFromClassifiers?: boolean;

  requestedExecutionDate?: string;

  customFieldsMetadata?: { label: string; name: string; type: string; value?: string }[];
  customFields?: PaymentCustomField[];
}

export interface IsPaymentHasAdditionalInfo {
  hasAdditionalInfo?: boolean;
  hasPayerInfo?: boolean;
  hasPayerAddressInfo?: boolean;
  hasPaymentIdentificationInfo?: boolean;
  hasPayerIdentificationInfo?: boolean;
  hasInitialPayerInfo?: boolean;
  hasInitialPayerIdentificationInfo?: boolean;
  hasInitialPayerIdentificationAddressInfo?: boolean;
  hasReceiverInfo?: boolean;
  hasReceiverAddressInfo?: boolean;
  hasReceiverAdditionalBankInfo?: boolean;
  hasReceiverCorrespondentBankInfo?: boolean;
  hasReceiverAdditionalCorrespondentBankInfo?: boolean;
  hasReceiverIdentificationInfo?: boolean;
  hasUltimateReceiverInfo?: boolean;
  hasUltimateReceiverAddressInfo?: boolean;
  hasUltimateReceiverIdentificationInfo?: boolean;
  hasOtherAdditionalInfo?: boolean;
}

export interface BeneficiaryAddressSuggestion {
  address: {
    addressLine?: string;
    buildingName?: string;
    buildingNumber?: string;
    city?: string;
    country?: string;
    countrySubDivision?: string;
    postCode?: string;
    street?: string;
  };
  status?: BeneficiaryAddressStatus;
}

export interface ValidateBeneficiaryAddressRequest {
  address: BeneficiaryAddressSuggestion['address'];
  paymentSystem: RestfulPayPaymentSystem;
}

export enum BeneficiaryAddressStatus {
  VALID = 'VALID',
  INVALID = 'INVALID'
}

export const assignIsPaymentHasAdditionalInfo = (paymentAdditionalInfo: PaymentAdditionalInfo): void => {
  const hasPayerAddressInfo =
    ['payerAddress', 'payerCountry'].some((key) => !!paymentAdditionalInfo[key]) && !['payerAddressMandatory'].some((key) => !!paymentAdditionalInfo[key]);

  const hasPaymentIdentificationInfo = ['paymentIdentification', 'purposeCode'].some((key) => !!paymentAdditionalInfo[key]);

  const hasPayerIdentificationInfo = [
    'payerIdentification',
    'payerIdentificationType',
    'payerIdentificationCodeType',
    'payerId',
    'payerCountryOfBirth',
    'payerProvinceOfBirth',
    'payerCityOfBirth',
    'payerIdentificationIssueCountry',
    'payerIdentificationIssueDate',
    'payerIdentificationExpiryDate'
  ].some((key) => !!paymentAdditionalInfo[key]);

  const hasInitialPayerIdentificationInfo = [
    'initialPayerIdentificationType',
    'initialPayerIdentificationCodeType',
    'initialPayerLegalEntityIdentifier',
    'initialPayerBic',
    'initialPayerId',
    'initialPayerBirthDate',
    'initialPayerProvinceOfBirth',
    'initialPayerCityOfBirth',
    'initialPayerCountryOfBirth'
  ].some((key) => !!paymentAdditionalInfo[key]);

  const hasInitialPayerIdentificationAddressInfo = [
    'initialPayerCountry',
    'initialPayerCity',
    'initialPayerProvince',
    'initialPayerStreetName',
    'initialPayerBuildingName',
    'initialPayerBuildingNumber',
    'initialPayerPostalCode'
  ].some((key) => !!paymentAdditionalInfo[key]);

  const hasInitialPayerInfo =
    !!hasInitialPayerIdentificationInfo ||
    ['initialPayerName', 'initialPayerPhoneNumber', 'initialPayerEmail'].some((key) => !!paymentAdditionalInfo[key]) ||
    !!hasInitialPayerIdentificationAddressInfo;

  const hasPayerInfo = !!hasPayerAddressInfo || !!hasPaymentIdentificationInfo || !!hasPayerIdentificationInfo || !!hasInitialPayerInfo;

  const hasReceiverPersonalInfo = ['beneficiaryRegistrationCountry', 'beneficiaryNationality', 'beneficiaryPhoneNumber', 'beneficiaryEmail'].some(
    (key) => !!paymentAdditionalInfo[key]
  );

  const hasReceiverAddressInfo = [
    'beneficiaryCountry',
    'beneficiaryProvince',
    'beneficiaryCity',
    'beneficiaryAddress',
    'beneficiaryStreetName',
    'beneficiaryBuildingNumber',
    'beneficiaryBuildingName',
    'beneficiaryPostalCode'
  ].some((key) => !!paymentAdditionalInfo[key]);

  const hasReceiverAdditionalCorrespondentBankInfo = [
    'additionalBeneficiaryCorrespondentBankCodeType',
    'additionalBeneficiaryCorrespondentBankCode',
    'accountWithBankCorrespondent'
  ].some((key) => !!paymentAdditionalInfo[key]);

  const hasReceiverCorrespondentBankInfo =
    [
      'correspondentBankIdentifier',
      'correspondentBankName',
      'correspondentBankAddress',
      'correspondentBankCode',
      'correspondentBankCountry',
      'intermediaryInstitutionIdentifier',
      'intermediaryInstitutionName',
      'intermediaryInstitutionAddress',
      'intermediaryInstitutionCode',
      'intermediaryInstitutionCountry'
    ].some((key) => !!paymentAdditionalInfo[key]) || hasReceiverAdditionalCorrespondentBankInfo;

  const hasReceiverIdentificationInfo = [
    'beneficiaryAccountType',
    'beneficiaryIdentification',
    'beneficiaryIdentificationType',
    'beneficiaryIdentificationCodeType',
    'beneficiaryIdentificationCode',
    'beneficiaryIdentificationDocument',
    'beneficiaryIdentificationName',
    'beneficiaryIdentificationSurname',
    'beneficiaryId',
    'beneficiaryBirthDate',
    'beneficiaryCountryOfBirth',
    'beneficiaryProvinceOfBirth',
    'beneficiaryCityOfBirth'
  ].some((key) => !!paymentAdditionalInfo[key]);

  const hasUltimateReceiverIdentificationInfo = [
    'ultimateBeneficiaryIdentification',
    'ultimateBeneficiaryIdentificationType',
    'ultimateBeneficiaryIdentificationCodeType',
    'ultimateBeneficiaryLegalEntityIdentifier',
    'ultimateBeneficiaryBic',
    'ultimateBeneficiaryId',
    'ultimateBeneficiaryCountryOfBirth',
    'ultimateBeneficiaryBirthDate',
    'ultimateBeneficiaryProvinceOfBirth',
    'ultimateBeneficiaryCityOfBirth'
  ].some((key) => !!paymentAdditionalInfo[key]);

  const hasUltimateReceiverAddressInfo = [
    'ultimateBeneficiaryCountry',
    'ultimateBeneficiaryCity',
    'ultimateBeneficiaryProvince',
    'ultimateBeneficiaryStreetName',
    'ultimateBeneficiaryBuildingName',
    'ultimateBeneficiaryBuildingNumber',
    'ultimateBeneficiaryPostalCode'
  ].some((key) => !!paymentAdditionalInfo[key]);

  const hasReceiverAdditionalBankInfo = ['additionalBeneficiaryBankCodeType', 'additionalBeneficiaryBankCode', 'accountInBeneficiaryBank'].some(
    (key) => !!paymentAdditionalInfo[key]
  );

  const hasUltimateReceiverInfo =
    !!hasUltimateReceiverIdentificationInfo ||
    !!hasUltimateReceiverAddressInfo ||
    ['ultimateBeneficiaryName', 'ultimateBeneficiaryPhoneNumber', 'ultimateBeneficiaryEmail'].some((key) => !!paymentAdditionalInfo[key]);

  const hasReceiverInfo =
    !!hasReceiverPersonalInfo ||
    !!hasReceiverAddressInfo ||
    !!hasReceiverCorrespondentBankInfo ||
    !!hasReceiverIdentificationInfo ||
    !!hasUltimateReceiverInfo ||
    !!hasReceiverAdditionalBankInfo;

  const hasValue = (key: string): boolean => {
    const value: unknown = paymentAdditionalInfo[key];
    return Array.isArray(value) ? value.length > 0 : !!value;
  };

  const hasOtherAdditionalInfo = ['sourceOfIncome', 'beneficiaryCity', 'routingCode1', 'routingCode2', 'customFields'].some(hasValue);

  const hasAdditionalInfo = !!hasPayerInfo || !!hasReceiverInfo || hasOtherAdditionalInfo;

  paymentAdditionalInfo = Object.assign(paymentAdditionalInfo, {
    hasAdditionalInfo,
    hasPayerInfo,
    hasPayerAddressInfo,
    hasPaymentIdentificationInfo,
    hasPayerIdentificationInfo,
    hasInitialPayerInfo,
    hasInitialPayerIdentificationInfo,
    hasInitialPayerIdentificationAddressInfo,
    hasReceiverInfo,
    hasReceiverAddressInfo,
    hasReceiverAdditionalBankInfo,
    hasReceiverCorrespondentBankInfo,
    hasReceiverAdditionalCorrespondentBankInfo,
    hasReceiverIdentificationInfo,
    hasUltimateReceiverInfo,
    hasUltimateReceiverAddressInfo,
    hasUltimateReceiverIdentificationInfo,
    hasOtherAdditionalInfo
  });
};

export const normalizePaymentCustomFields = (rawCustomFields: unknown, metadata?: { label: string; name: string; type: string }[]): PaymentCustomField[] => {
  const normalize = (name: string, value: unknown, label = name, type = ''): PaymentCustomField => ({
    label,
    name,
    type,
    value: value != null ? String(value) : undefined
  });

  if (Array.isArray(rawCustomFields)) {
    return rawCustomFields
      .filter((field): field is Record<string, unknown> => !!field && typeof field === 'object')
      .map((field, index) => {
        const name = typeof field.name === 'string' && field.name ? field.name : `customField${index}`;
        const label = typeof field.label === 'string' && field.label ? field.label : name;
        const type = typeof field.type === 'string' ? field.type : '';

        return normalize(name, 'value' in field ? field.value : undefined, label, type);
      });
  }

  if (rawCustomFields && typeof rawCustomFields === 'object') {
    const customFieldMap = rawCustomFields as Record<string, unknown>;

    if (Array.isArray(metadata) && metadata.length > 0) {
      return metadata
        .filter((field) => customFieldMap[field.name] != null)
        .map((field) => ({ label: field.label, name: field.name, type: field.type, value: String(customFieldMap[field.name]) }));
    }

    return Object.entries(customFieldMap).map(([name, value]) => normalize(name, value));
  }

  return [];
};

export enum SourceOfIncome {
  ALLOWANCE = 'ALLOWANCE',
  BUSINESS = 'BUSINESS',
  CRYPTO_CURRENCIES = 'CRYPTO_CURRENCIES',
  FUNDS_INVESTMENT = 'FUNDS_INVESTMENT',
  FUNDS_PENSION = 'FUNDS_PENSION',
  FUNDS_PROPERTY_RENTAL_INCOME = 'FUNDS_PROPERTY_RENTAL_INCOME',
  INHERITANCE = 'INHERITANCE',
  LEASES = 'LEASES',
  LOAN = 'LOAN',
  REAL_ESTATE_SALES = 'REAL_ESTATE_SALES',
  SALARY = 'SALARY',
  SAVINGS = 'SAVINGS',
  SCOLARSHIP = 'SCOLARSHIP',
  SECURITIES = 'SECURITIES',
  SOCIAL_BENEFITS = 'SOCIAL_BENEFITS',
  CROWDFUNDING = 'CROWDFUNDING',
  CRYPTO_CURRENCIES_ACTIVITIES = 'CRYPTO_CURRENCIES_ACTIVITIES',
  FUNDS_PROCEED_FROM_INITIAL_COIN_OFFERING = 'FUNDS_PROCEED_FROM_INITIAL_COIN_OFFERING',
  INVESTMENT = 'INVESTMENT',
  OPERATING_INCOME = 'OPERATING_INCOME',
  OTHER = 'OTHER',
  OWNERS_CAPITAL = 'OWNERS_CAPITAL'
}

export enum AccountType {
  CACC = 'CACC',
  CARD = 'CARD',
  CASH = 'CASH',
  CHAR = 'CHAR',
  CISH = 'CISH',
  COMM = 'COMM',
  CPAC = 'CPAC',
  LLSV = 'LLSV',
  LOAN = 'LOAN',
  MGLD = 'MGLD',
  MOMA = 'MOMA',
  NREX = 'NREX',
  ODFT = 'ODFT',
  ONDP = 'ONDP',
  OTHR = 'OTHR',
  SACC = 'SACC',
  SLRY = 'SLRY',
  SVGS = 'SVGS',
  TAXE = 'TAXE',
  TRAN = 'TRAN',
  TRAS = 'TRAS',
  VACC = 'VACC',
  NFCA = 'NFCA',
  CHAC = 'CHAC',
  OACC = 'OACC'
}

export enum PurposeCode {
  BKPP = 'BKPP',
  GDDS = 'GDDS',
  BLDM = 'BLDM',
  EDUC = 'EDUC',
  GIFT = 'GIFT',
  IVPT = 'IVPT',
  SAVG = 'SAVG',
  MDCS = 'MDCS',
  PCOM = 'PCOM',
  FUNX = 'FUNX',
  WEDX = 'WEDX',
  EMCO = 'EMCO',
  BUIN = 'BUIN',
  BONU = 'BONU',
  CASH = 'CASH',
  CBLK = 'CBLK',
  CCRD = 'CCRD',
  CORT = 'CORT',
  DCRD = 'DCRD',
  DIVI = 'DIVI',
  DVPM = 'DVPM',
  EPAY = 'EPAY',
  FCIN = 'FCIN',
  FCOL = 'FCOL',
  GP2P = 'GP2P',
  GOVT = 'GOVT',
  HEDG = 'HEDG',
  ICCP = 'ICCP',
  IDCP = 'IDCP',
  INTC = 'INTC',
  INTE = 'INTE',
  LBOX = 'LBOX',
  LOAN = 'LOAN',
  MP2B = 'MP2B',
  MP2P = 'MP2P',
  OTHR = 'OTHR',
  PENS = 'PENS',
  RPRE = 'RPRE',
  RRCT = 'RRCT',
  RVPM = 'RVPM',
  SALA = 'SALA',
  SECU = 'SECU',
  SSBE = 'SSBE',
  SUPP = 'SUPP',
  TAXS = 'TAXS',
  TRAD = 'TRAD',
  TREA = 'TREA',
  VATX = 'VATX',
  WHLD = 'WHLD',
  SWEP = 'SWEP',
  TOPG = 'TOPG',
  ZABA = 'ZABA',
  VOST = 'VOST',
  FCDT = 'FCDT',
  CIPC = 'CIPC',
  CONC = 'CONC',
  CGWV = 'CGWV'
}

export enum RoutingCodes {
  AUBSB = 'AUBSB',
  ATBLZ = 'ATBLZ',
  BRID = 'BRID',
  BRNM = 'BRNM',
  CACPA = 'CACPA',
  CNAPS = 'CNAPS',
  DEBLZ = 'DEBLZ',
  GRBIC = 'GRBIC',
  HKNCC = 'HKNCC',
  INFSC = 'INFSC',
  IENCC = 'IENCC',
  ITNCC = 'ITNCC',
  JPZGN = 'JPZGN',
  NZNCC = 'NZNCC',
  PLKNR = 'PLKNR',
  PTNCC = 'PTNCC',
  RUCBC = 'RUCBC',
  SGIBG = 'SGIBG',
  ZANCC = 'ZANCC',
  ESNCC = 'ESNCC',
  SESBA = 'SESBA',
  CHBCC = 'CHBCC',
  CHSIC = 'CHSIC',
  TWNCC = 'TWNCC',
  GBDSC = 'GBDSC',
  USPID = 'USPID',
  USABA = 'USABA',
  JMBRI = 'JMBRI',
  BDBRI = 'BDBRI',
  KRFC = 'KRFC'
}
